import React, { memo, useMemo, useCallback, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Field, FieldArray } from "@progress/kendo-react-form";
import { FaLongArrowAltRight, FaFilter, FaRegCommentDots, FaPlus, FaCopy, FaTimes } from "react-icons/fa";
import IconComponent from "../../../components/common/IconComponent";
import FormNumericTextField from "../../../components/formFields/FormNumericTextField";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import FormMultiSelectionFiled from "../../../components/formFields/FormMultiSelectionFiled";
import FormIncrementalSearch from "../../../components/formFields/FormIncrementalSearch";
import FormTextArea from "../../../components/formFields/FormTextArea";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { customizeHotelSearch, getAllActiveHotelRoomCategory, getMealPlanByHotelID, customizedAdvanceHotelSearchRoomCategoryWise, getHotelByID } from "../../hotel/services/hotel.services";
import AdvancedHotelSearchDialog from "./AdvancedHotelSearchDialog";
import moment from "moment";
import PriceBreakdownDialog from "./PriceBreakdownDialog";
import "../b2bmodule.scss";

interface HotelSelectionRowProps {
  namePrefix: string;
  startDate: any;
  nights: any;
  destination: number | string;
  prices: any[];
  noOfRooms: number;
  adults: number;
  aweb: number;
  cweb: number;
  cnb: number;
  infants: number;
  hotelId?: number;
  mealId?: number;
  roomId?: number;
  onFormChange: (name: string, event: any) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onNextNight: () => void;
  hotelsCount: number;
  comment?: string;
}

const HotelFieldComponent = (fieldRenderProps: any) => {
  const {
    hotelOptions,
    handleHotelSearch,
    handleHotelChange,
    onChange,
    onFormChange,
    namePrefix
  } = fieldRenderProps;

  return (
    <div className="d-flex flex-column">
      <div className="d-flex align-items-end">
        <div className="flex-grow-1">
          <FormIncrementalSearch
            {...fieldRenderProps}
            label="Hotel"
            wrapperClassName="m-0"
            placeholder="Search hotel..."
            fetchIncrementalData={handleHotelSearch}
            options={hotelOptions}
            onChange={(val: any) => {
              onChange({
                value: val?.value,
                target: { value: val }
              });
              onFormChange(`${namePrefix}.HotelName`, { value: val?.label });
              handleHotelChange(val);
            }}
          />
        </div>
        <Button type="button" className="field-icon-btn" onClick={fieldRenderProps.onFilterClick}>
          <IconComponent icon={FaFilter} style={{ fontSize: "14px" }} />
        </Button>
      </div>
    </div>
  );
};

const HotelSelectionRow = memo(({
  namePrefix,
  startDate,
  nights,
  destination,
  prices = [],
  noOfRooms,
  adults,
  aweb,
  cweb,
  cnb,
  infants,
  hotelId,
  mealId,
  roomId,
  onFormChange,
  onRemove,
  onDuplicate,
  onNextNight,
  hotelsCount,
  comment
}: HotelSelectionRowProps) => {
  const dispatch = useAppDispatch();

  const [isAdvanceSearchOpen, setIsAdvanceSearchOpen] = useState(false);
  const [extraHotelOptions, setExtraHotelOptions] = useState<any[]>([]);
  const [showCommentBox, setShowCommentBox] = useState(!!comment);

  // Selectors for dynamic data
  const { SearchedHotelList } = useAppSelector((state) => state.hotel);
  const [localRoomTypeOptions, setLocalRoomTypeOptions] = useState<any[]>([]);
  const [localMealPlanOptions, setLocalMealPlanOptions] = useState<any[]>([]);

  const [openPriceDialogIndex, setOpenPriceDialogIndex] = useState<number | null>(null);

  // Get base trip details for dynamic stay nights
  const totalNights = Number(nights) || 0;

  // 1. Generate Dynamic Stay Nights Options
  const stayNightOptions = useMemo(() => {
    const options = [];
    if (startDate && totalNights > 0) {
      for (let i = 1; i <= totalNights; i++) {
        const date = moment(startDate).add(i - 1, 'days');
        const ordinal = i === 1 ? "1st" : i === 2 ? "2nd" : i === 3 ? "3rd" : `${i}th`;
        const label = `${ordinal} N (${date.format('ddd DD MMM')})`;
        options.push({ label, value: label });
      }
    }
    return options;
  }, [startDate, totalNights]);

  // Hotel Pricing API Call
  const handleFetchRates = useCallback(async (hotelId: any, roomId: any, mealId: any, nightPrices: any[], forceOverwrite: boolean = false) => {
    if (!hotelId || !roomId) return;

    // Pass comma separated dates IF the API supports it, or individual calls?
    const dates = nightPrices.map(p => {
      const dateMatch = p.Date;
      return dateMatch ? moment(dateMatch).format("YYYY-MM-DD") : null;
    }).filter(Boolean).join(",");

    try {
      const payload = {
        HotelID: Number(hotelId),
        CategoryID: 0,
        RoomCategoryID: Number(roomId),
        MealTypeID: Number(mealId) || 0,
        FilterDate: dates
      };

      const response = await dispatch(customizedAdvanceHotelSearchRoomCategoryWise(payload)).unwrap();
      const resData = response?.Data;

      const updatedPrices = nightPrices.map(p => {
        const dayMatch = moment(p.Date).format("YYYY-MM-DD");
        // Match by FromDate (DD-MM-YYYY format from API)
        const rateInfo = (resData?.DateWiseData || []).find((d: any) => moment(d.FromDate, "DD-MM-YYYY").format("YYYY-MM-DD") === dayMatch);

        const shouldUpdatePrice = forceOverwrite || !p.GivenPrice || Number(p.GivenPrice) === 0;

        if (rateInfo) {
          const adultsVal = Number(adults) || 2;
          const roomsVal = Number(noOfRooms) || 1;
          const awebVal = Number(aweb) || 0;
          const cwebVal = Number(cweb) || 0;
          const cnbVal = Number(cnb) || 0;

          const singleRate = Number(rateInfo.SingleOccupancyRate) || 0;
          const doubleRate = Number(rateInfo.DoubleOccupancyRate) || 0;
          const extraPersonRate = Number(rateInfo.ExtraPersonRate) || 0;
          const childWithBedRate = Number(rateInfo.ChildWithBedRate) || 0;
          const childWithoutBedRate = Number(rateInfo.ChildWithoutBedRate) || 0;

          // 1, 2, or >2 adults logic
          const baseRoomPrice = (adultsVal === 1) ? singleRate : doubleRate;
          const extraAdultsFromMainPax = adultsVal > 2 ? (adultsVal - 2) : 0;
          const effectiveAWEBQuantity = awebVal + extraAdultsFromMainPax;

          const finalDayTotal = (baseRoomPrice + (effectiveAWEBQuantity * extraPersonRate) + (childWithBedRate * cwebVal) + (childWithoutBedRate * cnbVal)) * roomsVal;

          return {
            ...p,
            AdultPrice: shouldUpdatePrice ? baseRoomPrice : (p.AdultPrice || 0),
            AWEBPrice: shouldUpdatePrice ? extraPersonRate : (p.AWEBPrice || 0),
            CWEBPrice: shouldUpdatePrice ? childWithBedRate : (p.CWEBPrice || 0),
            CNBPrice: shouldUpdatePrice ? childWithoutBedRate : (p.CNBPrice || 0),
            OrgAdultPrice: baseRoomPrice,
            OrgAWEBPrice: extraPersonRate,
            OrgCWEBPrice: childWithBedRate,
            OrgCNBPrice: childWithoutBedRate,
            Rate: finalDayTotal,
            // ✅ SMART OVERWRITE: Protect manual price if not forced
            GivenPrice: shouldUpdatePrice ? finalDayTotal : p.GivenPrice
          };
        } else if (shouldUpdatePrice) {
          // ✅ NO RATE FOUND: Reset to 0 if overwrite is permitted
          return {
            ...p,
            AdultPrice: 0,
            AWEBPrice: 0,
            CWEBPrice: 0,
            CNBPrice: 0,
            OrgAdultPrice: 0,
            OrgAWEBPrice: 0,
            OrgCWEBPrice: 0,
            OrgCNBPrice: 0,
            Rate: 0,
            GivenPrice: 0
          };
        }
        return p;
      });
      onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });
    } catch (error) {
      console.error("Failed to fetch rates:", error);
    }
  }, [onFormChange, namePrefix]);

  // Fetch options for this specific hotel locally to prevent cross-row interference
  React.useEffect(() => {
    if (hotelId) {
      // Room categories
      dispatch(getAllActiveHotelRoomCategory({ HotelID: hotelId })).unwrap()
        .then((data: any) => {
          setLocalRoomTypeOptions(data?.map((room: any) => ({
            label: room.RoomCategoryName,
            value: room.ID
          })) || []);
        });

      // Meal plans
      dispatch(getMealPlanByHotelID({ HotelID: hotelId })).unwrap()
        .then((data: any) => {
          setLocalMealPlanOptions(data?.map((meal: any) => ({
            label: meal.MealType,
            value: meal.MealID
          })) || []);
        });
    } else {
      setLocalRoomTypeOptions([]);
      setLocalMealPlanOptions([]);
    }
  }, [hotelId, dispatch]);

  // ✅ CHANGE DETECTION TRACKER: Prevents automatic overwriting during hydration
  const prevIdsRef = React.useRef({ hotelId, roomId, mealId });

  // Trigger API call when selection changes
  React.useEffect(() => {
    const hasChanged =
      prevIdsRef.current.hotelId !== hotelId ||
      prevIdsRef.current.roomId !== roomId ||
      prevIdsRef.current.mealId !== mealId;

    if (hasChanged) {
      if (hotelId && roomId) {
        // User deliberately changed hotel/room/meal selection -> force update prices
        handleFetchRates(hotelId, roomId, mealId, prices, true);
      }
      prevIdsRef.current = { hotelId, roomId, mealId };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelId, roomId, mealId]);


  // 4. Reactive Pricing: Recalculate totals when occupancy OR rates change
  const priceHashRef = React.useRef("");

  React.useEffect(() => {
    if (!prices || prices.length === 0) return;

    // Use a hash of all values affecting the price calculation including the latest prices array
    const hash = JSON.stringify({ adults, noOfRooms, aweb, cweb, cnb, hotelId, roomId, mealId, prices });
    if (priceHashRef.current === hash) return;
    priceHashRef.current = hash;

    const adultsVal = Number(adults) || 2;
    const roomsVal = Number(noOfRooms) || 1;
    const awebVal = Number(aweb) || 0;
    const cwebVal = Number(cweb) || 0;
    const cnbVal = Number(cnb) || 0;

    let hasChange = false;
    const updatedPrices = prices.map(p => {
      // Re-apply occupancy logic locally using stored base rates
      const baseRoomPrice = Number(p.AdultPrice) || 0;
      const extraPersonRate = Number(p.AWEBPrice) || 0;
      const childWithBedRate = Number(p.CWEBPrice) || 0;
      const childWithoutBedRate = Number(p.CNBPrice) || 0;

      // extra adults from main pax logic (adults > 2)
      const extraAdultsFromMainPax = adultsVal > 2 ? (adultsVal - 2) : 0;
      const totalAweb = awebVal + extraAdultsFromMainPax;

      const newTotal = (baseRoomPrice + (totalAweb * extraPersonRate) + (childWithBedRate * cwebVal) + (childWithoutBedRate * cnbVal)) * roomsVal;

      if (p.Rate !== newTotal) {
        hasChange = true;
        return {
          ...p,
          Rate: newTotal,
          GivenPrice: p.GivenPrice === p.Rate ? newTotal : p.GivenPrice
        };
      }
      return p;
    });

    if (hasChange) {
      onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });
    }
  }, [
    adults,
    noOfRooms,
    aweb,
    cweb,
    cnb,
    namePrefix,
    onFormChange,
    prices,
    hotelId,
    roomId,
    mealId
  ]);

  // Sync dates in prices if startDate changes
  React.useEffect(() => {
    if (startDate && prices.length > 0) {
      let hasChange = false;
      const updatedPrices = prices.map((p, idx) => {
        // Find night number from property, label or index
        const nightNumMatch = p._label?.match(/(\d+)/);
        const nightNum = p.Night || (nightNumMatch ? parseInt(nightNumMatch[0]) : (idx + 1));

        const date = moment(startDate).add(nightNum - 1, 'days');
        const isoDate = date.toISOString();

        if (p.Date !== isoDate || !p._displayDate || !p._displayDay) {
          hasChange = true;
          return {
            ...p,
            Date: isoDate,
            _displayDate: date.format('DD MMM'),
            _displayDay: date.format('ddd')
          };
        }
        return p;
      });

      if (hasChange) {
        onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });
        // NEW: Refetch if we have hotel selection
        if (hotelId && roomId) {
          // Date shift update -> DO NOT overwrite manual prices
          handleFetchRates(hotelId, roomId, mealId, updatedPrices, false);
        }
      }
    }
  }, [startDate, namePrefix, onFormChange, hotelId, roomId, mealId]);

  // 2. Hotel Search Logic
  const handleHotelSearch = useCallback((searchText: string) => {
    dispatch(customizeHotelSearch({ HotelName: searchText }));
  }, [dispatch]);

  const hotelOptions = useMemo(() =>
    SearchedHotelList?.map(hotel => ({
      label: hotel.HotelName,
      value: hotel.ID,
      category: hotel.HotelCategory,
      url: hotel.URL
    })) || [],
    [SearchedHotelList]);

  // Combined options to ensure advanced search selection is visible
  const allHotelOptions = useMemo(() => {
    const combined = [...hotelOptions];
    extraHotelOptions.forEach(extra => {
      if (!combined.find(opt => opt.value === extra.value)) {
        combined.push(extra);
      }
    });
    return combined;
  }, [hotelOptions, extraHotelOptions]);

  // Hydration logic: if hotelId is provided but no label is in options, fetch it
  React.useEffect(() => {
    const isHydrated = (id: number) =>
      allHotelOptions.some(opt => opt.value === id);

    if (hotelId && !isHydrated(hotelId)) {
      dispatch(getHotelByID(hotelId)).unwrap()
        .then((data: any) => {
          if (data && data.HotelName) {
            setExtraHotelOptions([{
              label: data.HotelName,
              value: data.ID,
              category: data.HotelCategory
            }]);
          }
        })
        .catch(err => console.error("Hydration fetch failed:", err));
    }
  }, [hotelId, allHotelOptions, dispatch]);

  // 3. Cascading Updates on Hotel Selection
  const handleHotelChange = useCallback(() => {
    onFormChange(`${namePrefix}.MealPlanID`, { value: null });
    onFormChange(`${namePrefix}.MealPlanName`, { value: "" });
    onFormChange(`${namePrefix}.RoomTypeID`, { value: null });
    onFormChange(`${namePrefix}.RoomTypeName`, { value: "" });
  }, [onFormChange, namePrefix]);

  // Transform Room Category and Meal Type lists for dropdowns
  const roomTypeOptions = localRoomTypeOptions;
  const mealPlanOptions = localMealPlanOptions;

  const handlePriceSave = (data: any, index: number, syncAll: boolean) => {
    const updatedFields = {
      AdultPrice: data.AdultPrice,
      AWEBPrice: data.AWEBPrice,
      CWEBPrice: data.CWEBPrice,
      CNBPrice: data.CNBPrice,
      GivenPrice: data.total
    };

    if (syncAll) {
      const updatedPrices = prices.map((p: any) => ({ ...p, ...updatedFields }));
      onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });
    } else {
      const updatedItem = { ...prices[index], ...updatedFields };
      onFormChange(`${namePrefix}.NightPrices[${index}]`, { value: updatedItem });
    }
    setOpenPriceDialogIndex(null);
  };

  const extractNightNum = (s: string) => {
    if (typeof s !== "string") return Number(s) || 0;
    const match = s.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  return (
    <div className="b2b-quotation-module hotel-row-container d-flex flex-column flex-lg-row gap-0">
      <div className="hotel-details-col" style={{ paddingRight: "15px" }}>
        <div className="sembark-details-container">
          <div className="sembark-row-1">
            <div className="d-flex flex-column" style={{ flex: "1 1 180px" }}>
              <Field
                name={`${namePrefix}.Nights`}
                label="Stay Nights"
                wrapperClassName="m-0"
                placeholder="Select night(s)..."
                component={FormMultiSelectionFiled}
                options={stayNightOptions}
                onChange={(e: any) => {
                  const newNights = e.value || [];
                  const currentPrices = Array.isArray(prices) ? [...prices] : [];
                  const updatedPrices = newNights.map((night: any) => {
                    const nightLabel = typeof night === 'string' ? night : night?.label || night?.value || "";
                    const datePart = nightLabel.split('(')[1]?.replace(')', '') || "";
                    const dayPart = datePart.split(' ')[0] || "";
                    const dateOnly = datePart.replace(dayPart, '').trim() || "";
                    const isoDate = moment(`${dateOnly} ${moment(startDate).year()}`, "DD MMM YYYY").toISOString();

                    const existingByDate = currentPrices.find(p => p.Date === isoDate);

                    if (existingByDate) {
                      return { ...existingByDate, _label: nightLabel };
                    }

                    return {
                      AdultPrice: 0,
                      AWEBPrice: 0,
                      CWEBPrice: 0,
                      CNBPrice: 0,
                      OrgAdultPrice: 0,
                      OrgAWEBPrice: 0,
                      OrgCWEBPrice: 0,
                      OrgCNBPrice: 0,
                      Rate: 0,
                      GivenPrice: 0,
                      Date: isoDate,
                      Night: extractNightNum(nightLabel),
                      _displayDate: dateOnly,
                      _displayDay: dayPart,
                      _label: nightLabel
                    };
                  });
                  onFormChange(`${namePrefix}.Nights`, { value: newNights });
                  onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });

                  // NEW: Fetch rates for the updated nights if selection is already made
                  // Night count update -> DO NOT overwrite manual prices for existing nights
                  if (hotelId && roomId) {
                    handleFetchRates(hotelId, roomId, mealId, updatedPrices, false);
                  }
                }}
              />
            </div>

            <div className="d-flex flex-column" style={{ flex: "2 1 240px" }}>
              <Field
                name={`${namePrefix}.HotelID`}
                component={HotelFieldComponent}
                hotelOptions={allHotelOptions}
                handleHotelSearch={handleHotelSearch}
                handleHotelChange={handleHotelChange}
                onFilterClick={() => setIsAdvanceSearchOpen(true)}
                destination={destination}
                onFormChange={onFormChange}
                namePrefix={namePrefix}
              />
            </div>

            <div className="d-flex flex-column" style={{ flex: "1 1 160px" }}>
              <Field
                name={`${namePrefix}.MealPlanID`}
                label="Meal Plan"
                wrapperClassName="m-0"
                component={FormSelectionField}
                options={mealPlanOptions}
                onChange={(e: any) => {
                  onFormChange(`${namePrefix}.MealPlanID`, { value: e.value });
                  const opt = mealPlanOptions.find((o) => o.value === e.value);
                  if (opt) onFormChange(`${namePrefix}.MealPlanName`, { value: opt.label });
                }}
              />
            </div>

            <div className="d-flex flex-column" style={{ flex: "1 1 180px" }}>
              <Field
                name={`${namePrefix}.RoomTypeID`}
                label="Room Type"
                wrapperClassName="m-0"
                placeholder="Select..."
                component={FormSelectionField}
                options={roomTypeOptions}
                onChange={(e: any) => {
                  onFormChange(`${namePrefix}.RoomTypeID`, { value: e.value });
                  const opt = roomTypeOptions.find((o) => o.value === e.value);
                  if (opt) onFormChange(`${namePrefix}.RoomTypeName`, { value: opt.label });
                }}
              />
            </div>
          </div>

          <div className="pax-rooms-grid">
            <div className="pax-item-lux wide">
              <Field
                name={`${namePrefix}.Adults`}
                label="Pax/room (WoEB)"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="pax-item-lux wide">
              <Field
                name={`${namePrefix}.NoOfRooms`}
                label="No. of rooms"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="pax-item-lux">
              <Field
                name={`${namePrefix}.AWEB`}
                label="AWEB"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="pax-item-lux">
              <Field
                name={`${namePrefix}.CWEB`}
                label="CWEB"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="pax-item-lux">
              <Field
                name={`${namePrefix}.CNB`}
                label="CNB"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="pax-item-lux">
              <Field
                name={`${namePrefix}.Infants`}
                label="Infants"
                component={FormNumericTextField}
                min={0}
              />
            </div>
            <div className="comp-child-lux">
              <div className="k-label mb-1 fw-bold text-muted" style={{ fontSize: "12px" }}>Comp Child</div>
              <div className="fw-bold text-dark" style={{ fontSize: "12px", marginTop: "2px" }}>Upto 5y (OC)</div>
            </div>
          </div>
        </div>
      </div>

      <div className="hotel-prices-col">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <span className="fw-bold text-dark" style={{ fontSize: "17px" }}>Prices</span>
          <div className="d-flex gap-3 align-items-center me-1">
            <div className="cursor-pointer" onClick={() => {
              const nextState = !showCommentBox;
              setShowCommentBox(nextState);
              if (!nextState) {
                onFormChange(`${namePrefix}.Comment`, { value: "" });
              }
            }}>
              <IconComponent
                icon={FaRegCommentDots}
                className={`${showCommentBox ? 'text-primary' : 'text-muted opacity-50'}`}
                style={{ fontSize: "16px" }}
              />
            </div>
            {/* <div className="cursor-pointer" onClick={() => handleFetchRates(hotelId, roomId, mealId, prices, true)}>
              <IconComponent
                icon={FaSyncAlt}
                className="text-muted opacity-50"
                style={{ fontSize: "16px" }}
              />
            </div> */}
          </div>
        </div>

        <div className="prices-table-wrapper">
          <FieldArray
            name={`${namePrefix}.NightPrices`}
            component={(fieldArrayProps: any) => {
              const currentPrices = fieldArrayProps.value || [];
              const currentTotalRate = currentPrices.reduce((acc: number, p: any) => acc + (Number(p?.Rate) || 0), 0);
              const currentTotalGiven = currentPrices.reduce((acc: number, p: any) => acc + (Number(p?.GivenPrice) || 0), 0);

              return (
                <table className="prices-table">
                  <thead>
                    <tr>
                      <th style={{ width: "22%" }}>Date</th>
                      <th style={{ width: "34%", textAlign: "right" }}>
                        Rate <span className="unit">INR</span><span className="total-val">{currentTotalRate ? currentTotalRate.toLocaleString() : "0"}</span>
                      </th>
                      <th style={{ width: "10%" }}></th>
                      <th style={{ width: "34%", textAlign: "right" }}>
                        Given <span className="unit">INR</span><span className="total-val">{currentTotalGiven ? currentTotalGiven.toLocaleString() : "0"}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPrices.map((p: any, i: number) => {
                      const givenValue = Number(p.GivenPrice) || 0;
                      return (
                        <tr key={i}>
                          <td className="py-2">
                            <div className="row-date">{p._displayDate}</div>
                            <div className="row-day">{p._displayDay}</div>
                          </td>
                          <td className="text-muted align-middle text-end pe-2" style={{ fontSize: "13px" }}>
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <span className="unit" style={{ verticalAlign: "baseline" }}>INR</span>
                              {p.Rate ? p.Rate.toLocaleString() : "N/A"}
                            </div>
                          </td>
                          <td className="align-middle">
                            <div className="price-arrow">
                              <IconComponent icon={FaLongArrowAltRight} />
                            </div>
                          </td>
                          <td className="align-middle text-end">
                            <div
                              className="d-flex align-items-center justify-content-end gap-2 cursor-pointer"
                              onClick={() => setOpenPriceDialogIndex(i)}
                            >
                              <span className="unit" style={{ verticalAlign: "baseline" }}>INR</span>
                              <div className="given-price-display border rounded px-2 py-1 bg-white text-dark fw-bold shadow-sm hover-bg-light" style={{ width: "100px", minHeight: "30px" }}>
                                {givenValue ? givenValue.toLocaleString() : "0"}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            }}
          />
        </div>

        <div className="prices-action-row border-0 pt-1 mt-1">
          <div className="action-btn btn-next-night" onClick={onNextNight}>
            <IconComponent icon={FaPlus} style={{ fontSize: "11px" }} /> Next Night
          </div>
          <div className="action-btn" onClick={onDuplicate}>
            <IconComponent icon={FaCopy} style={{ fontSize: "11px" }} /> Duplicate
          </div>
          {hotelsCount > 1 && (
            <div className="action-btn btn-remove" onClick={onRemove}>
              <IconComponent icon={FaTimes} className="rem-x" style={{ fontSize: "12px" }} /> Remove
            </div>
          )}
        </div>

        {showCommentBox && (
          <div className="mt-2">
            <Field
              name={`${namePrefix}.Comment`}
              component={FormTextArea}
              rows={2}
            />
          </div>
        )}
      </div>

      {isAdvanceSearchOpen && (
        <AdvancedHotelSearchDialog
          isOpen={isAdvanceSearchOpen}
          onClose={() => setIsAdvanceSearchOpen(false)}
          destination={destination}
          startDate={startDate}
          selectedDates={(() => {
            const currentPrices = Array.isArray(prices) ? prices : [];
            const year = moment(startDate).year();

            return Array.from(new Set(
              currentPrices
                .map(p => {
                  if (!p?._displayDate || !startDate) return null;
                  const parsed = moment(`${p._displayDate} ${year}`, "DD MMM YYYY");
                  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
                })
                .filter((d): d is string => !!d)
            ));
          })()}
          initialPax={{
            adults: Number(adults) || 2,
            rooms: Number(noOfRooms) || 1,
            aweb: Number(aweb) || 0,
            cweb: Number(cweb) || 0,
            cnb: Number(cnb) || 0,
            infants: Number(infants) || 0
          }}

          onSelect={(hotel) => {
            const selectedOption = {
              label: hotel.HotelName,
              value: hotel.ID,
              category: hotel.HotelCategory
            };
            // 1. Set Hotel and fetch options
            setExtraHotelOptions([selectedOption]);

            // Pre-populate local options from the search result to prevent blank fields while re-fetching
            if (hotel.CalculatedBreakdown && hotel.CalculatedBreakdown.length > 0) {
              const uniqueRooms = Array.from(new Map(hotel.CalculatedBreakdown.map((b: any) => [b.roomId, b])).values());
              const uniqueMeals = Array.from(new Map(hotel.CalculatedBreakdown.map((b: any) => [b.mealId, b])).values());

              setLocalRoomTypeOptions(uniqueRooms.map((r: any) => ({ label: r.room, value: r.roomId })));
              setLocalMealPlanOptions(uniqueMeals.map((m: any) => ({ label: m.meal, value: m.mealId })));

              const first = hotel.CalculatedBreakdown[0];
              const mealId = first.mealId;
              const roomId = first.roomId;

              onFormChange(`${namePrefix}.HotelID`, { value: selectedOption.value });
              onFormChange(`${namePrefix}.HotelName`, { value: selectedOption.label });
              if (mealId) {
                onFormChange(`${namePrefix}.MealPlanID`, { value: mealId });
                const mOpt: any = uniqueMeals.find((m: any) => m.mealId === mealId);
                if (mOpt) onFormChange(`${namePrefix}.MealPlanName`, { value: mOpt.meal });
              }
              if (roomId) {
                onFormChange(`${namePrefix}.RoomTypeID`, { value: roomId });
                const rOpt: any = uniqueRooms.find((r: any) => r.roomId === roomId);
                if (rOpt) onFormChange(`${namePrefix}.RoomTypeName`, { value: rOpt.room });
              }

              // Sync PAX details from search dialog to main row
              if (hotel.SelectedPax) {
                onFormChange(`${namePrefix}.Adults`, { value: Number(hotel.SelectedPax.adults) || 2 });
                onFormChange(`${namePrefix}.NoOfRooms`, { value: Number(hotel.SelectedPax.rooms) || 1 });
                onFormChange(`${namePrefix}.AWEB`, { value: Number(hotel.SelectedPax.aweb) || 0 });
                onFormChange(`${namePrefix}.CWEB`, { value: Number(hotel.SelectedPax.cweb) || 0 });
                onFormChange(`${namePrefix}.CNB`, { value: Number(hotel.SelectedPax.cnb) || 0 });
              }

              // 2. Auto-fill nightly prices for ALL matching nights
              const currentPrices = Array.isArray(prices) ? [...prices] : [];
              const updatedPrices = currentPrices.map((p) => {
                const match = hotel.CalculatedBreakdown.find((d: any) => {
                  const breakdownDate = moment(d.date, ["DD-MM-YYYY", "YYYY-MM-DD"]).format("DD MMM");
                  return breakdownDate === p._displayDate;
                });

                if (match && !match.error) {
                  return {
                    ...p,
                    GivenPrice: match.total,
                    Rate: match.total,
                    AdultPrice: match.rates?.roomPrice || 0,
                    AWEBPrice: match.rates?.awebPrice || 0,
                    CWEBPrice: match.rates?.cwebPrice || 0,
                    CNBPrice: match.rates?.cnbPrice || 0,
                    OrgAdultPrice: match.rates?.roomPrice || 0,
                    OrgAWEBPrice: match.rates?.awebPrice || 0,
                    OrgCWEBPrice: match.rates?.cwebPrice || 0,
                    OrgCNBPrice: match.rates?.cnbPrice || 0
                  };
                }
                return p;
              });
              onFormChange(`${namePrefix}.NightPrices`, { value: updatedPrices });
            } else {
              onFormChange(`${namePrefix}.HotelID`, { value: selectedOption.value });
            }

            setIsAdvanceSearchOpen(false);
          }}
        />
      )}

      {openPriceDialogIndex !== null && (
        <PriceBreakdownDialog
          isOpen={openPriceDialogIndex !== null}
          onClose={() => setOpenPriceDialogIndex(null)}
          onSave={(data, syncAll) => handlePriceSave(data, openPriceDialogIndex, syncAll)}
          dateLabel={(() => {
            const item = prices[openPriceDialogIndex];
            return item ? `${item._displayDay} ${item._displayDate}` : "";
          })()}
          nextDateLabel={(() => {
            const item = prices[openPriceDialogIndex + 1];
            if (!item) return undefined;
            return `${item._displayDay} ${item._displayDate}`;
          })()}
          initialValues={{
            AdultPrice: prices[openPriceDialogIndex]?.AdultPrice || 0,
            AWEBPrice: prices[openPriceDialogIndex]?.AWEBPrice || 0,
            CWEBPrice: prices[openPriceDialogIndex]?.CWEBPrice || 0,
            CNBPrice: prices[openPriceDialogIndex]?.CNBPrice || 0
          }}
          quantities={{
            rooms: Number(noOfRooms) || 1,
            aweb: (Number(aweb) || 0) + (Number(adults) > 2 ? Number(adults) - 2 : 0),
            cweb: Number(cweb) || 0,
            cnb: Number(cnb) || 0
          }}
        />
      )}
    </div>
  );
});

export default HotelSelectionRow;
